import { useState, useCallback, useEffect } from "react";
import images from "@assets/images";
import { MOBILE_FLOOR_LAYOUTS } from "../constants/mobileFloorLayouts";
import "./MobileBlockPlan.css";

/**
 * Mapping loại căn → ảnh legend
 * 1pn_1.webp dùng cho cả 1PN+ và 1PN_GOC
 */
const BEDROOM_TYPE_LEGEND = [
  { bedroomType: "1PN",     icon: images.utilType1PN,  label: "1 PN"     },
  { bedroomType: "1PN+",    icon: images.utilType1PN1, label: "1 PN+"    },
  { bedroomType: "1PN_GOC", icon: images.utilType1PN1, label: "1 PN Góc" },
  { bedroomType: "2PN",     icon: images.utilType2PN,  label: "2 PN"     },
  { bedroomType: "2PN_2",   icon: images.utilType2PN,  label: "2 PN"     },
  { bedroomType: "3PN",     icon: images.utilType3PN,  label: "3 PN"     },
];

/**
 * MobileBlockPlan
 * Hiển thị mặt bằng mobile của 1 tầng, cho phép chuyển Toà A / Toà B.
 * - Tap 1 lần: highlight căn + hiện InfoPopup
 * - Tap 2 lần vào căn đang active: mở BedroomModal
 *
 * Props:
 *   layoutId   — id tầng (e.g. "3-7", "22")
 *   onAptClick — callback(apt) khi mở BedroomModal
 */
function MobileBlockPlan({ layoutId, onAptClick }) {
  const layoutData = MOBILE_FLOOR_LAYOUTS[layoutId] || {};
  const availableBlocks = Object.keys(layoutData);

  const [activeBlock, setActiveBlock] = useState(() => availableBlocks[0] || "B");
  const [tappedApt, setTappedApt] = useState(null); // { maCan, ...apt }

  // ── Reset block + selection khi layoutId thay đổi ──
  useEffect(() => {
    const blocks = Object.keys(MOBILE_FLOOR_LAYOUTS[layoutId] || {});
    setActiveBlock(blocks[0] || "B");
    setTappedApt(null);
  }, [layoutId]);

  const blockData = layoutData[activeBlock];

  const handleBlockChange = (block) => {
    setActiveBlock(block);
    setTappedApt(null);
  };

  const handleAptTap = useCallback(
    (e, maCan, apt) => {
      e.stopPropagation();
      if (tappedApt?.maCan === maCan) {
        // Tap lần 2 vào cùng căn → mở BedroomModal
        onAptClick({ maCan, block: activeBlock, ...apt });
        setTappedApt(null);
      } else {
        // Tap lần 1 → highlight + show InfoPopup
        setTappedApt({ maCan, ...apt });
      }
    },
    [tappedApt, activeBlock, onAptClick]
  );

  if (!blockData) return null;

  // Unique legend icons present in current block (dedupe theo ảnh — 1PN+ và 1PN_GOC dùng chung 1pn_1.webp)
  const seenIcons = new Set();
  const legendItems = BEDROOM_TYPE_LEGEND.filter((item) => {
    const exists = Object.values(blockData.apartments).some(
      (a) => a.bedroomType === item.bedroomType
    );
    if (exists && !seenIcons.has(item.icon)) {
      seenIcons.add(item.icon);
      return true;
    }
    return false;
  });

  const formatArea = (val) =>
    typeof val === "number" ? val.toFixed(2).replace(".", ",") : val;

  return (
    <div className="mb-plan">
      {/* Compass (La bàn) - Top Right Corner */}
      <img
        src={images.utilLaban}
        alt="La bàn"
        className="mb-plan__laban"
      />

      {/* ── Tab bar Toà A / Toà B ── */}
      {availableBlocks.length > 1 && (
        <div className="mb-plan__tabs">
          {availableBlocks.map((block) => (
            <button
              key={block}
              className={`mb-plan__tab${activeBlock === block ? " mb-plan__tab--active" : ""}`}
              onClick={() => handleBlockChange(block)}
              type="button"
            >
              Toà {block}
            </button>
          ))}
        </div>
      )}

      {/* ── Hướng + Plan viewport ── */}
      <div className="mb-plan__area" onClick={() => setTappedApt(null)}>
        {/* Hướng Bắc */}
        <img
          src={images.utilKdlTop}
          alt="KDL hướng Bắc"
          className="mb-plan__dir mb-plan__dir--top"
        />

        {/* Row: Tây | Plan | Đông */}
        <div className="mb-plan__middle-row">
          <img
            src={images.utilFaLeft}
            alt="Fa hướng Tây"
            className="mb-plan__dir mb-plan__dir--left"
          />

          <div className="mb-plan__viewport">
            <img
              key={`${layoutId}-${activeBlock}`}
              src={blockData.planImage}
              alt={`Mặt bằng Toà ${activeBlock} tầng ${layoutId}`}
              className="mb-plan__bg"
            />
            <svg
              viewBox={blockData.svgViewBox}
              className={`mb-plan__svg${tappedApt ? " has-active" : ""}`}
              onClick={(e) => { e.stopPropagation(); setTappedApt(null); }}
            >
              {Object.entries(blockData.apartments).map(([maCan, apt]) => (
                <path
                  key={maCan}
                  d={apt.svgPath}
                  pathLength="100"
                  className={`matbang-path-unit${tappedApt?.maCan === maCan ? " matbang-path-unit--active" : ""}`}
                  onClick={(e) => handleAptTap(e, maCan, apt)}
                />
              ))}
            </svg>

            {/* InfoPopup khi tap căn — hiện ngay trên viewport */}
            {tappedApt && (
              <div className="mb-plan__info-popup" onClick={(e) => e.stopPropagation()}>
                <button
                  className="mb-plan__info-close"
                  onClick={() => setTappedApt(null)}
                  type="button"
                  aria-label="Đóng"
                >✕</button>
                <p className="mb-plan__info-code">{tappedApt.maCan}</p>
                <p className="mb-plan__info-type">
                  {tappedApt.bedroomType?.replace("_", " ") ?? "Căn hộ"}
                </p>
                <div className="mb-plan__info-specs">
                  <span className="mb-plan__info-spec-label">Tim tường</span>
                  <span className="mb-plan__info-spec-value">{formatArea(tappedApt.timTuong)} m²</span>
                  <span className="mb-plan__info-spec-label">Thông thủy</span>
                  <span className="mb-plan__info-spec-value">{formatArea(tappedApt.thongThuy)} m²</span>
                </div>
                <button
                  className="mb-plan__info-detail-btn"
                  onClick={() => {
                    onAptClick({ ...tappedApt, block: activeBlock });
                    setTappedApt(null);
                  }}
                  type="button"
                >
                  Xem mặt bằng căn
                </button>
              </div>
            )}
          </div>

          <img
            src={images.utilKdlRight}
            alt="KDL hướng Đông"
            className="mb-plan__dir mb-plan__dir--right"
          />
        </div>

        {/* Hướng Nam */}
        <img
          src={images.utilGaBottom}
          alt="Ga hướng Nam"
          className="mb-plan__dir mb-plan__dir--bottom"
        />
      </div>

      {/* ── Bottom: Legend loại căn (căn giữa) ── */}
      <div className="mb-plan__bottom-bar">
        <div className="mb-plan__legend">
          {legendItems.map((item) => (
            <div key={item.bedroomType} className="mb-plan__legend-slot">
              <img
                src={item.icon}
                alt={item.label}
                className="mb-plan__legend-icon"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MobileBlockPlan;
